import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  closeDetailModal,
  getIsDevFeedbackDetailOpen,
  getSelectedDevFeedback,
  updateDevFeedback,
} from "../../features/devFeedback/devFeedbackSlice";
import { DevFeedbackSeverity, DevFeedbackStatus } from "../../types/devFeedback";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { t } from "../../locales";
import { toastStyles } from "../../static/styles";

const severityBadgeClass = (severity: DevFeedbackSeverity) => {
  switch (severity) {
    case "bug":
      return "bg-rose-600 hover:bg-rose-700 text-white";
    case "ui":
      return "bg-amber-500 hover:bg-amber-600 text-white";
    case "suggestion":
      return "bg-blue-600 hover:bg-blue-700 text-white";
    default:
      return "bg-gray-500 hover:bg-gray-600 text-white";
  }
};

const DevFeedbackDetailModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const open = useAppSelector(getIsDevFeedbackDetailOpen);
  const feedback = useAppSelector(getSelectedDevFeedback);
  const labels = t.devFeedback;

  const [status, setStatus] = useState<DevFeedbackStatus>("open");
  const [resolutionNote, setResolutionNote] = useState("");
  const [saving, setSaving] = useState(false);

  const statusOptions: { value: DevFeedbackStatus; label: string }[] = [
    { value: "open", label: labels.statusOpen },
    { value: "in_progress", label: labels.statusInProgress },
    { value: "resolved", label: labels.statusResolved },
    { value: "closed", label: labels.statusClosed },
  ];
  const severityLabel = (severity: DevFeedbackSeverity) => {
    const map: Record<DevFeedbackSeverity, string> = {
      bug: labels.severityBug,
      ui: labels.severityUi,
      suggestion: labels.severitySuggestion,
      other: labels.severityOther,
    };
    return map[severity] ?? severity;
  };

  useEffect(() => {
    if (feedback) {
      setStatus(feedback.status);
      setResolutionNote(feedback.resolution_note ?? "");
    }
  }, [feedback]);

  const handleOpenChange = (next: boolean) => {
    if (!next) dispatch(closeDetailModal());
  };

  const handleSave = async () => {
    if (!feedback) return;
    setSaving(true);
    const action: any = await dispatch(
      updateDevFeedback({ id: feedback.id, status, resolution_note: resolutionNote || null })
    );
    setSaving(false);
    const response = action.payload;
    if (response?.data?.status === "success") {
      toast.success(response.data.message || labels.updateSuccess, { ...(toastStyles as any) });
    } else {
      toast.error(response?.data?.message || labels.updateError, { ...(toastStyles as any) });
    }
  };

  return (
    <Dialog open={open && !!feedback} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#113F67]">
            {labels.modalTitle}
          </DialogTitle>
          {feedback ? (
            <DialogDescription>
              {feedback.page_path} · {moment(feedback.created_at).format("DD MMM YYYY, hh:mm:ss A")}
            </DialogDescription>
          ) : null}
        </DialogHeader>

        {feedback ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="text-xs uppercase font-semibold text-gray-500 mb-1">{labels.metaUser}</p>
                <p className="text-sm text-gray-900">{feedback.user?.name ?? labels.systemUser}</p>
              </div>
              <div>
                <p className="text-xs uppercase font-semibold text-gray-500 mb-1">{labels.severityLabel}</p>
                <Badge className={severityBadgeClass(feedback.severity)}>{severityLabel(feedback.severity)}</Badge>
              </div>
              <div>
                <p className="text-xs uppercase font-semibold text-gray-500 mb-1">{labels.metaTimestamp}</p>
                <p className="text-sm text-gray-900">
                  {moment(feedback.created_at).format("DD MMM YYYY, hh:mm:ss A")}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase font-semibold text-gray-500 mb-1">{labels.metaViewport}</p>
                <p className="text-sm text-gray-900">{feedback.viewport ?? "-"}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs uppercase font-semibold text-gray-500 mb-1">{labels.metaUrl}</p>
                <p className="text-sm text-gray-900 break-all">{feedback.page_url}</p>
              </div>
              {feedback.component_label && (
                <div>
                  <p className="text-xs uppercase font-semibold text-gray-500 mb-1">{labels.metaComponent}</p>
                  <p className="text-sm text-gray-900">{feedback.component_label}</p>
                </div>
              )}
              {feedback.element_selector && (
                <div>
                  <p className="text-xs uppercase font-semibold text-gray-500 mb-1">{labels.metaSelector}</p>
                  <p className="text-sm text-gray-900 font-mono break-all">{feedback.element_selector}</p>
                </div>
              )}
              {feedback.element_text && (
                <div className="md:col-span-2">
                  <p className="text-xs uppercase font-semibold text-gray-500 mb-1">{labels.metaElementText}</p>
                  <p className="text-sm text-gray-900 italic break-words">"{feedback.element_text}"</p>
                </div>
              )}
              {feedback.ip_address && (
                <div>
                  <p className="text-xs uppercase font-semibold text-gray-500 mb-1">{labels.metaIp}</p>
                  <p className="text-sm text-gray-900">{feedback.ip_address}</p>
                </div>
              )}
              {feedback.user_agent && (
                <div className="md:col-span-2">
                  <p className="text-xs uppercase font-semibold text-gray-500 mb-1">{labels.metaBrowser}</p>
                  <p className="text-sm text-gray-900 break-all">{feedback.user_agent}</p>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">{labels.messageSectionTitle}</h3>
              <p className="text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap">
                {feedback.message}
              </p>
            </div>

            <div className="border-t pt-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">{labels.statusSectionTitle}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="feedback-status">{labels.statusLabel}</Label>
                  <Select value={status} onValueChange={(v) => setStatus(v as DevFeedbackStatus)}>
                    <SelectTrigger id="feedback-status" className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="feedback-resolution-note">{labels.resolutionNoteLabel}</Label>
                <Textarea
                  id="feedback-resolution-note"
                  rows={3}
                  placeholder={labels.resolutionNotePlaceholder}
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button variant="outline" onClick={() => dispatch(closeDetailModal())} disabled={saving}>
                {labels.closeButton}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              >
                {saving ? labels.saving : labels.saveButton}
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default DevFeedbackDetailModal;
